import styles from './CartLineItem.scss';

import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from 'src/components/Button/Button';
import { useCart } from 'src/components/Cart/UseCart';
import { Grid } from 'src/components/Grid/Grid';
import { Icon, IconType } from 'src/components/Icon/Icon';
import { CartDetailModel } from '../Cart/models';
import { ContentLinkV2 } from '../ContentLinkV2/ContentLinkV2';
import { RemoveCartItemModal } from '../RemoveCartItemModal/RemoveCartItemModal';
import { TextV2 } from '../TextV2/TextV2';
import { CartItem } from './CartItem/CartItem';

interface CartLineItemProps {
  cartData?: CartDetailModel;
}

export const CartLineItem: FC<CartLineItemProps> = ({ cartData }) => {
  if (!cartData) return null;

  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();
  const { removeProductById } = useCart();

  const { bundle, softwarePlan, products } = cartData;
  const upsellList = products?.map(product => {
    return { product, removeProductById: () => removeProductById(product.id) };
  });
  const hardwareList = bundle?.hardware?.map(product => {
    return { product };
  }) || [];
  const cartList = [...hardwareList, ...upsellList];
  const linkToRoute = cartData.bundle?.pricingPageLink;

  const heading = bundle?.cartBusinessType
    ? t('cart-page.heading-with-solution-title', { solutionTitle: bundle?.cartBusinessType, bundleName: bundle?.name })
    : bundle?.name;

  return (
    <Grid xs={12} md={12} item>
      <TextV2 value={t('cart-page.title')} variant="h4" tagName="h4" className={styles.mainTitle} />
      <Grid md={12} className={styles.cartLineItemContainer}>
        {linkToRoute?.title ? (
          <ContentLinkV2 entry={linkToRoute} hideTitle data-test="cart-link">
            <TextV2 value={heading} tagName="h3" variant="h3-bold" className={styles.cartLineItemContainerTitle} />
          </ContentLinkV2>
          ) : <TextV2 value={heading} tagName="h3" variant="h3-bold" className={styles.cartLineItemContainerTitle} data-test="cart-heading" />
        }
        <Button id="close-cart-item" className={styles.cartLineItemContainerClose} aria-label={t('close')} onClick={() => setIsOpen(!isOpen)}><Icon type={IconType.CLOSE} /></Button>
        <TextV2 value={t('cart-page.hardware')} tagName="p" variant="eyebrow" className={styles.cartLineItemContainerSubTitle} />
        <Grid>
          <CartItem cartList={cartList} />
          <div className={styles.monthlyCartContainer}>
            <TextV2 value={t('cart-page.monthly-plan')} tagName="p" variant="eyebrow" className={styles.monthlyCartContainerHeader} />
            <div>
              <div className={styles.monthlyCartContainerRegister}>
                <TextV2 value={softwarePlan?.name} tagName="p" variant="h5" className={styles.monthlyCartContainerTitle} data-test="plan-name" />
                <TextV2 value={t('cart-page.monthly-price', { price: softwarePlan?.monthly })} tagName="p" className={styles.monthlyCartContainerPrice} variant="body" />
              </div>
              <TextV2 value={t('cart-page.free-text')} tagName="p" variant="body-small" />
              {softwarePlan && (
                <div data-test="pricings">
                  <TextV2 value={t('cart-page.in-person-transactions', { percentage: softwarePlan.transactions.keyedIn.percentage, price: softwarePlan.transactions.keyedIn.cost })} tagName="p" variant="body-small" />
                  <TextV2 value={t('cart-page.keyed-in-transactions', { percentage: softwarePlan.transactions.inPerson.percentage, price: softwarePlan.transactions.inPerson.cost })} tagName="p" variant="body-small" />
                </div>
              )}
            </div>
          </div>
          <RemoveCartItemModal modalState={[isOpen, () => setIsOpen(!isOpen)]} />
        </Grid>
      </Grid>
    </Grid>
  );
};

CartLineItem.displayName = 'CartLineItem';
